import isApp from './ua';

function getPlainPath(path: string) {
  let plainPath = path.replace(/\/[a-z]{2,3}-[A-Z]{2}\//, '/');

  if (!plainPath.endsWith('/')) {
    plainPath += '/';
  }

  return plainPath;
}

function isLogin(path: string) {
  return path === '/login/';
}

function isSignup(path: string) {
  return path === '/register/';
}

function isBuyCrypto(path: string) {
  return path === '/fiat/purchase/crypto/';
}

function isMarkets(path: string) {
  return [
    '/data/markets/',
    '/data/markets/collect/',
    '/data/markets/contract/',
    '/data/markets/spot/',
    '/data/markets/reference/',
  ].includes(path);
}

function isSpot(path: string) {
  return /^\/trade\/spot\/([A-Z]+\/[A-Z]+\/)?$/.test(path);
}

function isInversePerpetual(path: string) {
  return /^\/trade\/inverse\/([A-Z]+\/)?$/.test(path);
}

function isUSDTPerpetual(path: string) {
  return /^\/trade\/usdt\/([A-Z]+USDT\/)?$/.test(path);
}

function isInverseFutures(path: string) {
  return /^\/trade\/inverse\/futures\/[A-Z]+_(Q|BIQ)\/$/.test(path);
}

function isByfi(path: string) {
  return ['/earn/', '/earn/home/'].includes(path);
}

function isDefiMining(path: string) {
  return path === '/earn/defi-mining/';
}

function isDualAssetMining(path: string) {
  return path === '/earn/dual-asset-mining/';
}

function isLaunchpool(path: string) {
  return path === '/earn/launchpool/';
}

function isFlexibleStaking(path: string) {
  return path === '/earn/flexible-staking/';
}

function isAccount(path: string) {
  return path === '/user/assets/home/';
}

function isSpotAccount(path: string) {
  return [
    // Spot account
    '/user/assets/home/spot/',
    // Spot records
    '/user/assets/records/trade-spot/',
    '/user/assets/records/trade-spot/deposit/',
    '/user/assets/records/trade-spot/withdraw/',
    '/user/assets/records/trade-spot/transfer/',
    '/user/assets/records/trade-spot/fiat/',
    '/user/assets/records/trade-spot/other/',
    // Spot orders
    '/user/assets/order/spot-orders/',
    '/user/assets/order/spot-orders/active/',
    '/user/assets/order/spot-orders/conditional/',
    '/user/assets/order/spot-history/',
    '/user/assets/order/spot-history/active/',
  ].includes(path);
}

function isDerivativesAccount(path: string) {
  return [
    // Derivatives account
    '/user/assets/home/trading/',
    // Derivatives records
    '/user/assets/records/trade/',
    '/user/assets/records/trade/deposit/',
    '/user/assets/records/trade/exchange/',
    '/user/assets/records/trade/other/',
    // Derivatives orders
    '/user/assets/order/all-orders/',
    '/user/assets/order/all-orders/inverse/',
    '/user/assets/order/all-orders/usdt/',
    '/user/assets/order/all-orders/futures/',
    '/user/assets/order/closed-pnl/',
    '/user/assets/order/closed-pnl/inverse/',
    '/user/assets/order/closed-pnl/usdt/',
    '/user/assets/order/closed-pnl/futures/',
    '/user/assets/order/trade-history/',
    '/user/assets/order/trade-history/inverse/',
    '/user/assets/order/trade-history/usdt/',
    '/user/assets/order/trade-history/futures/',
  ].includes(path);
}

function isByfiAccount(path: string, url: URL) {
  return (
    // ByFi account
    (path === '/user/assets/home/financial/' &&
      url.searchParams.get('faTransfer') !== 'true') ||
    [
      // ByFi records
      '/earn/wallet-records/',
      // ByFi orders
      '/user/assets/order/financial-orders/',
      '/user/assets/order/financial-orders/mining-order/',
      '/user/assets/order/financial-defi-orders/',
      '/user/assets/order/financial-defi-orders/defi-order/',
      '/user/assets/order/financial-dual-asset-orders/',
      '/user/assets/order/financial-dual-asset-orders/dual-asset-order/',
      '/user/assets/order/financial-launchpool-orders/',
      '/user/assets/order/financial-launchpool-orders/launchpool-order/',
      '/user/assets/order/financial-flexible-staking-orders/',
      '/user/assets/order/financial-flexible-staking-orders/flexible-staking-order/',
    ].includes(path)
  );
}

function isTransfer(path: string, url: URL) {
  return (
    path === '/user/assets/home/financial/' &&
    url.searchParams.get('faTransfer') === 'true'
  );
}

function isReferral(path: string) {
  return path === '/referral/';
}

function isKYC(path: string) {
  return path === '/user/accounts/auth/personal/';
}

function isAboutUs(path: string) {
  return path === '/app/aboutus/aboutus/';
}

function isNotification(path: string) {
  return path === '/notifications';
}

const prefix = 'bybitapp://open';
export const NOTIFICATION_PAGE_DEEP_LINK = `/message/home`;

// eslint-disable-next-line consistent-return
export function getNativePath(to: string) {
  // const prefix = 'bybitapp://open';
  const url = new URL(to, window.location.href);
  const path = getPlainPath(url.pathname);

  if (isLogin(path)) {
    return { path: `${prefix}/login?fromWeb=1`, topPage: false };
  }

  if (isSignup(path)) {
    return {
      path: `${prefix}/signup?directlyRegister=true&fromWeb=1`,
      topPage: false,
    };
  }

  if (isBuyCrypto(path)) {
    return { path: `${prefix}/fiat/buycrypto`, topPage: false };
  }

  if (isMarkets(path)) {
    return { path: `${prefix}/home?tab=1`, topPage: true };
  }

  if (isSpot(path)) {
    const pair = path.replace('/trade/spot/', '');
    return {
      path: `${prefix}/home?tab=3${
        pair ? `&symbol=${pair.split('/').join('')}` : ''
      }`,
      topPage: true,
    };
  }

  if (isInversePerpetual(path)) {
    const pair = path.replace('/trade/inverse/', '').replace('/', '');
    return {
      path: `${prefix}/home?tab=2${pair ? `&symbol=${pair}` : ''}`,
      topPage: true,
    };
  }

  if (isUSDTPerpetual(path)) {
    const pair = path.replace('/trade/usdt/', '').replace('/', '');
    return {
      path: `${prefix}/home?tab=2${pair ? `&symbol=${pair}` : ''}`,
      topPage: true,
    };
  }

  if (isInverseFutures(path)) {
    const pair = path
      .replace('/trade/inverse/futures/', '')
      // Very fragile!!!
      .replace('_Q/', '1231')
      .replace('_BIQ/', '0325');
    return {
      path: `${prefix}/home?tab=2${pair ? `&symbol=${pair}` : ''}`,
      topPage: true,
    };
  }

  if (isByfi(path)) {
    return { path: `${prefix}/byficenter`, topPage: false };
  }

  if (isDefiMining(path)) {
    return { path: `${prefix}/byficenter?type=1`, topPage: false };
  }

  if (isDualAssetMining(path)) {
    return { path: `${prefix}/byficenter?type=2`, topPage: false };
  }

  if (isLaunchpool(path)) {
    return { path: `${prefix}/byficenter?type=3`, topPage: false };
  }

  if (isFlexibleStaking(path)) {
    return { path: `${prefix}/byficenter?type=4`, topPage: false };
  }

  if (isAccount(path)) {
    return { path: `${prefix}/assets_account`, topPage: true };
  }

  if (isSpotAccount(path)) {
    return {
      path: `${prefix}/assets_account?account=1`,
      topPage: true,
    };
  }

  if (isDerivativesAccount(path)) {
    return {
      path: `${prefix}/assets_account?account=2`,
      topPage: true,
    };
  }

  if (isByfiAccount(path, url)) {
    return {
      path: `${prefix}/assets_account?account=3`,
      topPage: true,
    };
  }

  if (isTransfer(path, url)) {
    const coin = url.searchParams.get('faCoin');
    return {
      path: `${prefix}/transfer${coin ? `?coin=${coin}` : ''}`,
      topPage: false,
    };
  }

  if (isReferral(path)) {
    return { path: `${prefix}/referral`, topPage: false };
  }

  if (isKYC(path)) {
    return { path: `${prefix}/user/verify/kyc`, topPage: false };
  }

  if (isAboutUs(path)) {
    return { path: `${prefix}/aboutus`, topPage: false };
  }

  if (isNotification(path)) {
    return { path: `${prefix}/message/home`, topPage: false };
  }
}

export async function navigateApp(url: string) {
  if (isApp()) {
    const nativePath = getNativePath(url);
    if (nativePath) {
      try {
        // Might throw in old versions of the app
        await window.BybitHecate.ready();
        await window.BybitHecate.Router.push({ path: nativePath.path });
        if (nativePath.topPage) window.BybitHecate.Router.pop2Home();
        // eslint-disable-next-line no-empty
      } catch {
        window.location.href = url;
      }
    }
  }
}

export const navigateToAPP = async (url: string, closePage = false) => {
  await window.BybitHecate.ready();
  await window.BybitHecate.Router.push({ path: `${prefix}${url}` });
  if (closePage) {
    window.BybitHecate.Router.pop();
  }
};
